import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { type TicketCategory, type TicketSolution, type TicketState } from "@/gql/graphql";
import type { RootState } from "@/store";

export interface TicketsReducerInterface {
  search: string;
  status: TicketState[];
  category: Nullable<TicketCategory>;
  solution: Nullable<TicketSolution>;
  owners: SmallUser[];
  nextStepBy: Nullable<SmallUser>;
  reporters: SmallUser[];
  priority: Nullable<number>;
  customerId: Nullable<string>;
  doneStart: Nullable<Date>;
  doneStop: Nullable<Date>;
  insertedStart: Nullable<Date>;
  insertedStop: Nullable<Date>;
  dueStart: Nullable<Date>;
  dueStop: Nullable<Date>;
  boardView: boolean;
  boardGrouping: string;
  filterAttrs: Record<string, AttributeFilterType>;
  team: Nullable<TeamRefWithName>;
}

const initialState: TicketsReducerInterface = {
  search: "",
  status: ["NEW", "ACCEPTED", "IN_PROGRESS", "QUESTION"],
  category: null,
  solution: null,
  owners: [],
  nextStepBy: null,
  reporters: [],
  priority: null,
  customerId: null,
  doneStart: null,
  doneStop: null,
  insertedStart: null,
  insertedStop: null,
  dueStart: null,
  dueStop: null,
  boardView: JSON.parse(localStorage.getItem("ticketBoardView") || "false"),
  boardGrouping: localStorage.getItem("ticketBoardGrouping") || "category",
  filterAttrs: {},
  team: null,
};

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    resetFilters(state) {
      state.search = "";
      state.status = ["NEW", "ACCEPTED", "IN_PROGRESS", "QUESTION"];
      state.category = null;
      state.solution = null;
      state.owners = [];
      state.reporters = [];
      state.nextStepBy = null;
      state.priority = null;
      state.customerId = null;
      state.doneStart = null;
      state.doneStop = null;
      state.insertedStart = null;
      state.insertedStop = null;
      state.dueStart = null;
      state.dueStop = null;
      state.team = null;
    },
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setStatus(state, action: PayloadAction<TicketState[]>) {
      state.status = action.payload;
    },
    setOwner(state, action: PayloadAction<SmallUser[]>) {
      state.owners = action.payload;
    },
    setNextStepBy(state, action: PayloadAction<Nullable<SmallUser>>) {
      state.nextStepBy = action.payload;
    },
    setReporters(state, action: PayloadAction<SmallUser[]>) {
      state.reporters = action.payload;
    },
    setPriority(state, action: PayloadAction<Nullable<number>>) {
      state.priority = action.payload;
    },
    setCategory(state, action: PayloadAction<Nullable<TicketCategory>>) {
      state.category = action.payload;
    },
    setSolution(state, action: PayloadAction<Nullable<TicketSolution>>) {
      state.solution = action.payload;
    },
    setCustomerId(state, { payload }: PayloadAction<Nullable<string>>) {
      state.customerId = payload;
    },
    setDoneStart(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.doneStart = payload;
    },
    setDoneStop(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.doneStop = payload;
    },
    setInsertedStart(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.insertedStart = payload;
    },
    setInsertedStop(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.insertedStop = payload;
    },
    setDueStart(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.dueStart = payload;
    },
    setDueStop(state, { payload }: PayloadAction<Nullable<Date>>) {
      state.dueStop = payload;
    },
    setTeam(state, { payload }: PayloadAction<Nullable<TeamRefWithName>>) {
      state.team = payload;
    },
    switchView(state) {
      state.boardView = !state.boardView;
      localStorage.setItem("ticketBoardView", JSON.stringify(state.boardView));
    },
    setBoardGrouping(state, { payload }: PayloadAction<string>) {
      state.boardGrouping = payload;
      localStorage.setItem("ticketBoardGrouping", payload);
    },
    setAttributeFilter(state, action: PayloadAction<{ name: string; value: Nilable<AttributeFilterType> }>) {
      if (!action.payload.value) {
        delete state.filterAttrs[action.payload.name];
      } else {
        state.filterAttrs[action.payload.name] = action.payload.value;
      }
    },
  },
});

export const {
  resetFilters,
  setSearch,
  setStatus,
  setOwner,
  setNextStepBy,
  setReporters,
  setPriority,
  setCategory,
  setSolution,
  setCustomerId,
  setDoneStart,
  setDoneStop,
  setInsertedStart,
  setInsertedStop,
  switchView,
  setAttributeFilter,
  setDueStart,
  setDueStop,
  setTeam,
  setBoardGrouping,
} = ticketsSlice.actions;
export const ticketsSelector = (state: RootState) => state.tickets;
export default ticketsSlice.reducer;
